import { getZIndexClass } from "@shared/lib/z-index"

/**
 * Input Component Styles
 *
 * This file contains all styles needed for input components:
 * - TYPOGRAPHY - Labels and descriptions for inputs
 * - INTERACTIVE - Buttons and interactive elements
 * - THRESHOLD - Field width sizes (tiny, small, medium, large)
 * - INPUTS - Input field styling (borders, padding, etc.)
 * - SELECTS - Select dropdown styling
 * - COMPOUND_INPUT - Multi-part input wrappers
 * - DROPDOWN - Dropdown menus for selectors
 * - SEARCH - Search input specific styling
 * - ENTITY - Entity selector specific styling
 * - RADIO - Radio button styling
 *
 * Input components are self-contained and don't import from sheets/steps.
 * For layout spacing and structural styles, see /shared/components/sheets/steps/styles.ts
 */

// Core design tokens for inputs
const COMMON_INPUT_CLASSES = "placeholder:font-light placeholder:opacity-50 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"

// Typography for input labels and descriptions
export const TYPOGRAPHY = {
  label: "text-xs font-medium leading-tight",
  labelLarge: "text-sm text-foreground font-normal leading-tight",
  description: "text-xs text-teal-300 font-light leading-tight tracking-tight",
  descriptionLarge: "text-sm text-teal-300 font-light leading-tight tracking-tight",
} as const

// Interactive elements for inputs
export const INTERACTIVE = {
  button: "flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors",
  buttonBase: "flex-1 flex gap-2 items-center justify-start px-3 py-1 rounded-lg transition-all",
} as const

// Field sizes (used for input width sizing)
export const THRESHOLD = {
  fields: {
    tiny: "flex-none w-[60px]",        // Small counters
    small: "flex-none w-[110px]",      // Compact inputs
    medium: "flex-none w-[8.75rem]",   // Standard inputs (default)
    large: "flex-1 max-w-[174px]",     // Wide inputs
  },
} as const

// Input components
export const INPUTS = {
  base: `[border-radius:10px] ${COMMON_INPUT_CLASSES}`,
  currency: `pl-7 [border-radius:10px] ${COMMON_INPUT_CLASSES}`,
  currencyWithSelect: `pl-7 [border-top-right-radius:0] [border-bottom-right-radius:0] [border-top-left-radius:10px] [border-bottom-left-radius:10px] border-r-0 ${COMMON_INPUT_CLASSES}`,
  quantity: `[border-radius:0] border-r-0 ${COMMON_INPUT_CLASSES}`,
  range: `border-0 p-0 h-auto text-sm bg-transparent focus-visible:ring-0 text-center w-full ${COMMON_INPUT_CLASSES}`,
  rangeContainer: "h-9 bg-background border border-border rounded-xl flex items-center px-3 gap-1",
  rangeInputWrapper: "flex-1 h-6 flex items-center justify-center rounded-md pb-0.5 hover:bg-muted/20 transition-colors",
  customUnit: `h-9 text-sm border-border/30 [border-radius:10px]`,
  numberInput: `[border-radius:10px] pr-8 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`,
} as const

// Select components
export const SELECTS = {
  currency: `w-24 rounded-l-none rounded-r-[10px]`,
  currencyContent: "min-w-24 max-h-60 overflow-auto p-0",
  unit: `w-24 rounded-l-none rounded-r-[10px]`,
  unitContent: "w-36 max-h-96 overflow-auto p-0",
} as const


// Compound input components (multi-part inputs like currency + selector)
export const COMPOUND_INPUT = {
  wrapper: `flex relative rounded-[10px] ring-1 ring-input transition-all focus-within:ring-foreground`,
  labelSectionBase: `flex items-center justify-center px-3 bg-white rounded-l-[10px] border-r border-input`,
  labelSectionNarrow: "w-12", // for "per"
  labelSectionWide: "w-24", // for "for a pack of"
  inputSection: "border-0 border-r border-input focus:outline-none focus:border-input",
  selectSection: "border-0 focus:outline-none focus:ring-0",
  currencySymbol: "absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-foreground",
} as const


// Dropdown components
export const DROPDOWN = {
  container: `absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden min-w-full w-max max-w-sm ${getZIndexClass('POPOVER')}`,
  itemHover: "hover:bg-muted transition-colors cursor-pointer px-4 py-2",
  addButton: "bg-primary-light cursor-pointer px-4 py-2.5 flex items-center gap-2",
  sectionLabel: "px-3 py-1.5",
  sectionLabelText: "text-xs font-mono uppercase tracking-wide opacity-60",
  emptyState: "px-4 py-3 text-sm opacity-60",
} as const

// Search components
export const SEARCH = {
  icon: "absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none",
  iconSize: "w-4 h-4",
  input: "rounded-[10px] border-input/50 pl-10",
} as const

// Entity selector specific
export const ENTITY = {
  textMuted: "text-sm text-muted-foreground tracking-tight opacity-60",
  textSecondary: "text-sm opacity-60",
  itemContainer: "flex flex-col items-start",
  fieldContainer: "flex flex-col",
} as const

// Radio button components
export const RADIO = {
  container: "flex gap-2 items-start",
  input: "accent-foreground focus:ring-0 h-4 w-4 border-input",
  textContainer: "flex flex-col items-start text-sm",
  label: "cursor-pointer",
  description: "text-xs text-teal-300",
} as const
